import { useCallback, useEffect, useState } from "react";
import { adminProductsV3Service } from "../../../../services/adminProductsV3Service";
import type { ProductMetadata } from "../../../../types/productMetadata";
import type { AdminProductItem } from "./useAdminProductsList";

function buildKey(product: AdminProductItem): string {
  return `${product.productType}-${product.providerId}-${product.id}`;
}

/**
 * Manages session-local metadata tag overrides for admin products.
 *
 * `dataVersion` is the authoritative server-refresh signal. Overrides are
 * cleared only when it bumps (i.e. after a successful reload fetches fresh
 * data). Do NOT pass a value derived from array identity here — optimistic
 * updates elsewhere (e.g. visibility changes in useAdminProductsList) must
 * not wipe pending tag edits.
 */
export function useAdminProductMetadata(dataVersion: number) {
  const [overrides, setOverrides] = useState<Map<string, ProductMetadata>>(new Map());
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setOverrides(new Map());
  }, [dataVersion]);

  const getMetadata = useCallback(
    (product: AdminProductItem): ProductMetadata | null => {
      const key = buildKey(product);
      return overrides.get(key) ?? product.settings;
    },
    [overrides]
  );

  const isSaving = useCallback(
    (product: AdminProductItem): boolean => {
      return savingKeys.has(buildKey(product));
    },
    [savingKeys]
  );

  const updateTag = useCallback(
    async (
      product: AdminProductItem,
      tagKey: keyof ProductMetadata,
      value: string | undefined
    ) => {
      const key = buildKey(product);
      const current = overrides.get(key) ?? product.settings ?? {};
      const updated: ProductMetadata = { ...current };

      if (!value) {
        delete updated[tagKey];
      } else {
        (updated as any)[tagKey] = value;
      }

      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(key, updated);
        return next;
      });

      setSavingKeys((prev) => new Set(prev).add(key));
      try {
        const payload = Object.keys(updated).length > 0 ? updated : null;
        await adminProductsV3Service.patchProductMetadata(
          product.productType,
          product.providerId,
          product.id,
          payload
        );
      } catch (err) {
        console.error("Failed to save tag:", err);
        setOverrides((prev) => {
          const next = new Map(prev);
          if (Object.keys(current).length === 0) {
            next.delete(key);
          } else {
            next.set(key, current as ProductMetadata);
          }
          return next;
        });
      } finally {
        setSavingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [overrides]
  );

  return { getMetadata, isSaving, updateTag };
}
